import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import React from "react";
import { auth } from "../../../core/auth";
import { env } from "../../../env";
import { dateFormatter } from "../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../shared/utils/format-response-error";

/*
 * Bulk Sandata re-submission from a spreadsheet.
 *
 * The endpoints below are called through plain axios rather than the typed
 * `api` client because they are not in the checked-in generated schema yet —
 * the same situation as the HHAX re-submit call in ClockedVisitsGraphQLRoute.
 * Re-running `pnpm openapi:local` against a server on this branch will add
 * them, at which point these local mirrors of the server message types can be
 * replaced by the generated ones.
 */

/** Mirrors SandataResubmitEligibility in src/messages/SandataResubmit.ts. */
type SandataResubmitEligibility =
  | "eligible"
  | "invalid_id"
  | "duplicate"
  | "not_found"
  | "not_sandata_contract"
  | "no_caregiver"
  | "rate_limited"
  | "no_credentials";

/** Mirrors SandataResubmitPreviewRow. Dates arrive as ISO strings. */
type PreviewRow = {
  rowNumber: number;
  rawValue: string;
  visitInstanceId: number | null;
  eligibility: SandataResubmitEligibility;
  ineligibleReason: string | null;
  visitDate: string | null;
  startTime: string | null;
  endTime: string | null;
  clockinTime: string | null;
  clockoutTime: string | null;
  clockinType: string | null;
  clockoutType: string | null;
  patientId: number | null;
  patientName: string | null;
  caregiverId: number | null;
  caregiverName: string | null;
  officeId: number | null;
  officeName: string | null;
  contractTypeName: string | null;
  serviceCodeId: number | null;
  serviceCode: string | null;
  state: string | null;
  lastSubmittedAt: string | null;
  submissionCompleted: boolean;
  submissionErrors: string[];
  submissionsToday: number;
};

/** Mirrors SandataResubmitPreview. */
type Preview = {
  fileName: string;
  idColumn: string;
  totalRows: number;
  eligibleCount: number;
  ineligibleCount: number;
  rows: PreviewRow[];
};

/** Mirrors SandataResubmitCreateJobResponse. */
type CreateJobResponse = {
  jobId: number;
  visitCount: number;
  unroutableCount: number;
};

/** Mirrors SandataResubmitJobProgress. */
type JobProgress = {
  jobId: number;
  status: "running" | "stopped" | "completed" | "error";
  totalVisits: number;
  submitted: number;
  failed: number;
  skipped: number;
  pendingItems: number;
  errorMessage: string | null;
};

const MAX_FILE_ROWS = 500;

const ELIGIBILITY_DISPLAY: Record<
  SandataResubmitEligibility,
  { label: string; colorScheme: string }
> = {
  eligible: { label: "Ready", colorScheme: "green" },
  invalid_id: { label: "Bad id", colorScheme: "red" },
  duplicate: { label: "Duplicate", colorScheme: "gray" },
  not_found: { label: "Not found", colorScheme: "red" },
  not_sandata_contract: { label: "Not Sandata", colorScheme: "orange" },
  no_caregiver: { label: "No caregiver", colorScheme: "orange" },
  rate_limited: { label: "Daily limit", colorScheme: "yellow" },
  no_credentials: { label: "No credentials", colorScheme: "orange" },
};

function agencyPath(path: string) {
  const authState = auth.getAuthOrFail();
  return (
    `${env.API_URL}agencies/${authState.data.agency.id}` +
    `/agency_members/${authState.data.agencyMember.id}/${path}`
  );
}

function authHeaders() {
  return { Authorization: `Token ${auth.getAuthOrFail().tokens.authToken}` };
}

function formatDate(value: string | null) {
  if (value === null) return "-";
  try {
    return dateFormatter.toDate(value);
  } catch {
    return "-";
  }
}

function formatTime(value: string | null) {
  if (value === null) return "-";
  try {
    return dateFormatter.toTime(value);
  } catch {
    return "-";
  }
}

function formatDateTime(value: string | null) {
  if (value === null) return "-";
  try {
    return dateFormatter.toDateTime(value);
  } catch {
    return "-";
  }
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a job is queued so the caller can refresh its visit list. */
  onSubmitted: () => void;
};

export function SandataResubmitUploadModal(props: Props) {
  const toast = useToast();
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const [jobId, setJobId] = React.useState<number | null>(null);
  const [onlyProblems, setOnlyProblems] = React.useState(false);

  // Reset on every open so a previous file's preview never leaks into a new run.
  React.useEffect(() => {
    if (props.isOpen) {
      setPreview(null);
      setJobId(null);
      setOnlyProblems(false);
    }
  }, [props.isOpen]);

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<Preview> => {
      const data = new FormData();
      data.append("file", file, file.name);
      const response = await axios.post<Preview>(
        agencyPath("sandata_visits/resubmit/preview"),
        data,
        { headers: authHeaders() }
      );
      return response.data;
    },
    onSuccess: (result) => setPreview(result),
    onError: (error: unknown) => {
      toast({
        title: "Could not read the file",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        duration: 10000,
        isClosable: true,
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (visitInstanceIds: number[]): Promise<CreateJobResponse> => {
      const response = await axios.post<CreateJobResponse>(
        agencyPath("sandata_visits/resubmit/jobs"),
        { visitInstanceIds, fileName: preview?.fileName ?? null },
        { headers: authHeaders() }
      );
      return response.data;
    },
    onSuccess: (result) => {
      setJobId(result.jobId);
      props.onSubmitted();
      if (result.unroutableCount > 0) {
        toast({
          title: "Some visits were not queued",
          description:
            `${result.unroutableCount} visit(s) no longer match any Sandata credentials ` +
            `and were skipped.`,
          status: "warning",
          position: "top-right",
          duration: 10000,
          isClosable: true,
        });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Re-submission failed to start",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        duration: 10000,
        isClosable: true,
      });
    },
  });

  // Poll while the job has work left. Sandata submits a whole batch per set of
  // credentials, so this is usually one or two items and finishes in under a
  // minute; `refetchInterval` stops once nothing is pending.
  const progressQuery = useQuery<JobProgress>({
    queryKey: ["sandata_resubmit_job", jobId],
    enabled: jobId !== null,
    queryFn: async () => {
      const response = await axios.get<JobProgress>(
        agencyPath(`sandata_visits/resubmit/jobs/${jobId}`),
        { headers: authHeaders() }
      );
      return response.data;
    },
    // TanStack v5 hands the whole query here, not the data.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data === undefined || data.pendingItems > 0 ? 3000 : false;
    },
  });

  const eligibleIds = React.useMemo(
    () =>
      (preview?.rows ?? [])
        .filter((row) => row.eligibility === "eligible" && row.visitInstanceId !== null)
        .map((row) => row.visitInstanceId as number),
    [preview]
  );

  const visibleRows = React.useMemo(
    () =>
      onlyProblems
        ? (preview?.rows ?? []).filter((row) => row.eligibility !== "eligible")
        : preview?.rows ?? [],
    [preview, onlyProblems]
  );

  function handleChooseFile() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.xlsx,.xls";
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file !== undefined) {
        uploadMutation.mutate(file);
      }
    };
    input.click();
  }

  const isDone =
    progressQuery.data !== undefined &&
    progressQuery.data.pendingItems === 0 &&
    progressQuery.data.status !== "running";

  // Only the approval table needs the full viewport -- it carries fifteen columns
  // of visit detail. The upload prompt and the progress panel are a few lines
  // each and read badly stretched across it, so the modal starts narrow and
  // widens once there is a table to show.
  const isReviewing = jobId === null && preview !== null;

  return (
    <Modal
      isOpen={props.isOpen}
      scrollBehavior="inside"
      size={isReviewing ? "6xl" : "xl"}
      onClose={props.onClose}
    >
      <ModalOverlay />
      <ModalContent maxW={isReviewing ? "95vw" : undefined}>
        <ModalHeader>Re-submit Sandata EVV visits from a file</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {jobId !== null ? (
            <JobProgressPanel isDone={isDone} progress={progressQuery.data} />
          ) : preview === null ? (
            <UploadInstructions
              isLoading={uploadMutation.isPending}
              onChooseFile={handleChooseFile}
            />
          ) : (
            <Stack spacing={4}>
              <PreviewSummary preview={preview} />
              <Checkbox
                isChecked={onlyProblems}
                isDisabled={preview.ineligibleCount === 0}
                onChange={(event) => setOnlyProblems(event.target.checked)}
              >
                Show only rows that cannot be re-submitted
              </Checkbox>
              <PreviewTable rows={visibleRows} />
            </Stack>
          )}
        </ModalBody>

        <ModalFooter>
          {jobId !== null ? (
            <Button isDisabled={!isDone} onClick={props.onClose}>
              {isDone ? "Close" : "Submitting…"}
            </Button>
          ) : (
            <HStack spacing={3}>
              <Button variant="ghost" onClick={props.onClose}>
                Cancel
              </Button>
              {preview !== null && (
                <>
                  <Button
                    isDisabled={approveMutation.isPending}
                    variant="outline"
                    onClick={handleChooseFile}
                  >
                    Upload a different file
                  </Button>
                  <Button
                    colorScheme="blue"
                    isDisabled={eligibleIds.length === 0}
                    isLoading={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(eligibleIds)}
                  >
                    Approve &amp; re-submit {eligibleIds.length} visit
                    {eligibleIds.length === 1 ? "" : "s"}
                  </Button>
                </>
              )}
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function UploadInstructions(props: { isLoading: boolean; onChooseFile: () => void }) {
  return (
    <Stack py={4} spacing={5}>
      <Text>
        Upload a CSV or Excel file listing the visits to re-submit. The file needs a column named{" "}
        <Code>Visit Instance ID</Code> (<Code>visit_id</Code> and <Code>id</Code> are also accepted)
        holding the visit ids from the <b>Visit ID</b> column of this page. Any other columns are
        ignored, so an export of this page can be uploaded as-is.
      </Text>
      <Text color="gray.600" fontSize="sm">
        Up to {MAX_FILE_ROWS} rows per file. Nothing is submitted until you review and approve the
        list on the next step — to change the selection, edit the file and upload it again.
      </Text>
      <Box>
        <Button colorScheme="blue" isLoading={props.isLoading} onClick={props.onChooseFile}>
          Choose file
        </Button>
      </Box>
    </Stack>
  );
}

function PreviewSummary(props: { preview: Preview }) {
  const { preview } = props;
  return (
    <Stack spacing={3}>
      <Flex align="center" gap={3} wrap="wrap">
        <Text fontWeight="semibold">{preview.fileName}</Text>
        <Badge colorScheme="gray">{preview.totalRows} rows</Badge>
        <Badge colorScheme="green">{preview.eligibleCount} ready</Badge>
        {preview.ineligibleCount > 0 && (
          <Badge colorScheme="orange">{preview.ineligibleCount} cannot be re-submitted</Badge>
        )}
        <Text color="gray.500" fontSize="sm">
          read from column <Code>{preview.idColumn}</Code>
        </Text>
      </Flex>
      {preview.eligibleCount === 0 && (
        <Alert status="warning">
          <AlertIcon />
          None of the rows in this file can be re-submitted. Check the reasons below, fix the file,
          and upload it again.
        </Alert>
      )}
    </Stack>
  );
}

function PreviewTable(props: { rows: PreviewRow[] }) {
  return (
    <TableContainer maxH="55vh" overflowY="auto">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
          <Tr>
            <Th>Row</Th>
            <Th>Visit ID</Th>
            <Th>Status</Th>
            <Th>Visit Date</Th>
            <Th>Visit Time</Th>
            <Th>Patient</Th>
            <Th>Caregiver</Th>
            <Th>Clock In</Th>
            <Th>Clock Out</Th>
            <Th>Contract</Th>
            <Th>Service Code</Th>
            <Th>Office</Th>
            <Th>Last Submitted</Th>
            <Th isNumeric>Today</Th>
            <Th>Current Error</Th>
          </Tr>
        </Thead>
        <Tbody>
          {props.rows.map((row) => (
            <PreviewTableRow key={row.rowNumber} row={row} />
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function PreviewTableRow(props: { row: PreviewRow }) {
  const { row } = props;
  const display = ELIGIBILITY_DISPLAY[row.eligibility];
  const isEligible = row.eligibility === "eligible";
  const errorText = row.submissionErrors.join("; ");

  return (
    <Tr bg={isEligible ? undefined : "gray.50"} opacity={isEligible ? 1 : 0.75}>
      <Td color="gray.500">{row.rowNumber}</Td>
      <Td>{row.visitInstanceId ?? row.rawValue}</Td>
      <Td>
        <Tooltip isDisabled={row.ineligibleReason === null} label={row.ineligibleReason ?? ""}>
          <Badge colorScheme={display.colorScheme}>{display.label}</Badge>
        </Tooltip>
      </Td>
      <Td>{formatDate(row.startTime)}</Td>
      <Td whiteSpace="nowrap">
        {row.startTime === null ? "-" : `${formatTime(row.startTime)} - ${formatTime(row.endTime)}`}
      </Td>
      <Td>{row.patientName ?? "-"}</Td>
      <Td>{row.caregiverName ?? "-"}</Td>
      <Td>{formatTime(row.clockinTime)}</Td>
      <Td>{formatTime(row.clockoutTime)}</Td>
      <Td>{row.contractTypeName ?? "-"}</Td>
      <Td>{row.serviceCode ?? "-"}</Td>
      <Td>{row.officeName ?? "-"}</Td>
      <Td whiteSpace="nowrap">{formatDateTime(row.lastSubmittedAt)}</Td>
      <Td isNumeric>{row.submissionsToday}</Td>
      <Td maxW="320px">
        {errorText === "" ? (
          "-"
        ) : (
          <Tooltip label={errorText}>
            <Text isTruncated>{errorText}</Text>
          </Tooltip>
        )}
      </Td>
    </Tr>
  );
}

function JobProgressPanel(props: { progress: JobProgress | undefined; isDone: boolean }) {
  const { progress, isDone } = props;

  if (progress === undefined) {
    return (
      <Flex align="center" gap={3} py={8}>
        <Spinner />
        <Text>Queuing the re-submission…</Text>
      </Flex>
    );
  }

  return (
    <Stack py={4} spacing={4}>
      <Text fontWeight="semibold">
        {isDone ? "Re-submission finished" : "Re-submitting to Sandata…"}
      </Text>
      {!isDone && <Progress isIndeterminate colorScheme="blue" size="sm" />}
      <Flex gap={3} wrap="wrap">
        <Badge colorScheme="gray">{progress.totalVisits} visits</Badge>
        <Badge colorScheme="green">{progress.submitted} submitted</Badge>
        {progress.failed > 0 && <Badge colorScheme="red">{progress.failed} failed</Badge>}
        {progress.skipped > 0 && <Badge colorScheme="yellow">{progress.skipped} skipped</Badge>}
      </Flex>
      {progress.errorMessage !== null && (
        <Alert status="error">
          <AlertIcon />
          {progress.errorMessage}
        </Alert>
      )}
      {isDone && (
        <Alert status="info">
          <AlertIcon />
          Sandata processes submissions asynchronously — per-visit results appear on this page once
          their result comes back.
        </Alert>
      )}
    </Stack>
  );
}
